import React from 'react';
import { css } from '@tanium/react-emotion';
import { Action, RibbonAction as BaseRibbonAction } from '@tanium/react-ribbon';

export interface ActionArgs<TItem> {
  index: number;
  itemData: TItem;
}

export type ActionEvent<TItem> = ActionArgs<TItem>;

export interface RibbonAction<TItem> extends Pick<BaseRibbonAction, 'alwaysVisible' | 'icon'> {
  /**
   * Function with `itemData` and `index` as arguments that returns a `boolean`.
   * If it returns `false`, the action will not be available in the ribbon.
   */
  isAvailable?: (args: ActionArgs<TItem>) => boolean;
  /**
   * Callback to invoke when the action is invoked.
   */
  onAction: (event: ActionEvent<TItem>) => void;
  /**
   * The label to use for the action. This label is used in a button if no icon is specified.
   * It may also be used in a tooltip and for accessibility purposes.
   * For row-contextualized labeling, a function with `itemData` and `index` as arguments
   * that returns the label can be used instead of a `string`.
   */
  label: string | ((args: ActionArgs<TItem>) => string);
}

interface Props<TItem> {
  actions: RibbonAction<TItem>[];
  index: number;
  itemData: TItem;
  isHovering: boolean;
}

const actionsContainerStyle = css({
  width: 0,
});

const actionsWrapperStyle = css({
  float: 'right',
  paddingRight: 16,
  whiteSpace: 'nowrap',
});

const ActionsInner = <TItem extends unknown>({
  actions,
  index,
  itemData,
  isHovering,
}: Props<TItem>) => {
  if (!actions) {
    return null;
  }
  const params: ActionArgs<TItem> = {
    itemData,
    index,
  };
  return (
    <div className={actionsContainerStyle}>
      <div className={actionsWrapperStyle}>
        {actions.map((props, i) => {
          if (props.isAvailable && !props.isAvailable(params)) {
            return null;
          }
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
          const onAction = () => {
            if (props.onAction) {
              props.onAction(params);
            }
          };
          const actionLabel = typeof props.label === 'function' ? props.label(params) : props.label;
          return (
            <Action
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              {...props}
              label={actionLabel}
              onAction={onAction}
              visible={isHovering}
            />
          );
        })}
      </div>
    </div>
  );
};

export const Actions = React.memo(ActionsInner);
